import { config } from './config';
import { t } from './i18n';
import type { TranslationKey } from './i18n';
import {
  AiQuotaStatusOut,
  AnalyzeRequest,
  AnalyzeResponse,
  AppApiPortalPortalSchemasUsageStatsOut,
  BudgetUpdateInput,
  ChatUrlOut,
  HubSeatOut,
  JoinableOrgOut,
  LoginInfoProbeOut,
  MonthlyUsageOut,
  OrgInfoOut,
  OrgJoinOut,
  OrgSignupOut,
  PendingMemberOut,
  PortalAccessRequestOut,
  PortalBrandingOut,
  PortalMcpLogOut,
  PortalOrgMemberOut,
  PortalServerDetailOut,
  PortalServerOut,
  PortalUserOut,
  PortalWorkflowDetailOut,
  PortalWorkflowExecutionOut,
  PortalWorkflowOut,
  PlatformRequestPortalOut,
  PortalVisualizationDataOut,
  ServerUserOut,
  WorkflowGenerationRequestCreate,
  WorkflowGenerationRequestOut,
} from './types/apiRequests';

const API_URL = config.api.baseUrl;
const DEFAULT_TIMEOUT = 30000;

const ERROR_MESSAGES: Record<number, TranslationKey> = {
  400: 'errors.api.badRequest',
  401: 'errors.api.unauthorized',
  403: 'errors.api.forbidden',
  404: 'errors.api.notFound',
  409: 'errors.api.conflict',
  429: 'errors.api.tooMany',
  500: 'errors.api.serverError',
};

const API_MESSAGE_TRANSLATIONS: Record<string, TranslationKey> = {
  'Seat limit reached for current plan.': 'errors.api.seatLimitReached',
  'Only pending requests can be cancelled.': 'errors.api.onlyPendingCancel',
};

async function fetchApi(path: string, options: RequestInit = {}, token?: string) {
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), DEFAULT_TIMEOUT);

  try {
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
      ...((options.headers || {}) as Record<string, string>),
    };
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }
    const res = await fetch(`${API_URL}${path}`, {
      ...options,
      headers,
      signal: controller.signal,
    });
    if (!res.ok) {
      let serverMessage: string | null = null;
      try {
        const body = await res.json();
        if (body?.message) serverMessage = body.message;
      } catch { /* empty */ }

      if (process.env.NODE_ENV === 'development') {
        console.error(`API ${res.status}: ${serverMessage || res.statusText}`);
      }
      const translationKey = serverMessage ? API_MESSAGE_TRANSLATIONS[serverMessage] : undefined;
      const statusKey = ERROR_MESSAGES[res.status];
      const userMessage = translationKey ? t(translationKey) : statusKey ? t(statusKey) : (serverMessage || t('errors.genericError'));
      const error: Error & { status?: number } = new Error(userMessage);
      error.status = res.status;
      throw error;
    }
    if (res.status === 204) {
      return undefined;
    }
    return res.json();
  } catch (e) {
    if (e instanceof DOMException && e.name === 'AbortError') {
      throw new Error(t('errors.api.timeout'));
    }
    throw e;
  } finally {
    clearTimeout(timeoutId);
  }
}

export const api = {
  getMe: (token: string): Promise<PortalUserOut> =>
    fetchApi('/portal/me', {}, token),

  getServers: (token: string): Promise<PortalServerOut[]> =>
    fetchApi('/portal/servers', {}, token),

  getServerDetail: (token: string, name: string): Promise<PortalServerDetailOut> =>
    fetchApi(`/portal/servers/${name}`, {}, token),

  instantAdd: (token: string, serverName: string, environmentName?: string): Promise<PortalServerDetailOut> =>
    fetchApi(`/portal/servers/${serverName}/instant-add${environmentName ? `?environment_name=${environmentName}` : ''}`, { method: 'POST' }, token),

  removeServer: (token: string, serverName: string): Promise<void> =>
    fetchApi(`/portal/servers/${serverName}/remove`, { method: 'DELETE' }, token),

  requestAccess: (
    token: string,
    serverName: string,
    data: { request_type: string; environment_url?: string; environment_name?: string; message?: string }
  ): Promise<PortalAccessRequestOut> =>
    fetchApi(`/portal/servers/${serverName}/request-access`, {
      method: 'POST',
      body: JSON.stringify(data),
    }, token),

  getMyRequests: (token: string): Promise<PortalAccessRequestOut[]> =>
    fetchApi('/portal/my-requests', {}, token),

  getMyWorkflowGenerationRequests: (token: string): Promise<WorkflowGenerationRequestOut[]> =>
    fetchApi('/portal/my-workflow-generation-requests', {}, token),

  getMyPlatformRequests: (token: string): Promise<PlatformRequestPortalOut[]> =>
    fetchApi('/portal/my-platform-requests', {}, token),

  cancelAccessRequest: (token: string, requestId: string): Promise<PortalAccessRequestOut> =>
    fetchApi(`/portal/my-requests/${requestId}/cancel`, { method: 'PUT' }, token),

  cancelPlatformRequest: (token: string, requestId: string): Promise<PlatformRequestPortalOut> =>
    fetchApi(`/portal/my-platform-requests/${requestId}/cancel`, { method: 'PUT' }, token),

  getMyOrg: (token: string): Promise<OrgInfoOut> =>
    fetchApi('/portal/my-org', {}, token),

  requestPlatformTier: (
    token: string,
    tier: string,
    message?: string
  ): Promise<PlatformRequestPortalOut> =>
    fetchApi('/portal/platform-request', {
      method: 'POST',
      body: JSON.stringify({ requested_tier: tier, message: message || null }),
    }, token),

  searchOrgMembers: (token: string, query: string): Promise<PortalOrgMemberOut[]> =>
    fetchApi(`/portal/org-members?q=${encodeURIComponent(query)}`, {}, token),

  listPendingMembers: (token: string): Promise<PendingMemberOut[]> =>
    fetchApi('/portal/pending-members', {}, token),

  decidePendingMember: (
    token: string,
    approvalId: string,
    decision: 'approve' | 'reject',
    note?: string,
  ): Promise<PendingMemberOut> =>
    fetchApi(`/portal/pending-members/${approvalId}/decide`, {
      method: 'POST',
      body: JSON.stringify({ decision, note: note || null }),
    }, token),

  getSeats: (token: string): Promise<HubSeatOut[]> =>
    fetchApi('/portal/seats', {}, token),

  addSeat: (token: string, data: { auth0_user_id: string; auth0_email: string }): Promise<HubSeatOut> =>
    fetchApi('/portal/seats', {
      method: 'POST',
      body: JSON.stringify(data),
    }, token),

  deleteSeat: (token: string, seatId: string): Promise<void> =>
    fetchApi(`/portal/seats/${seatId}`, { method: 'DELETE' }, token),

  toggleSeatChat: (token: string, seatId: string): Promise<HubSeatOut> =>
    fetchApi(`/portal/seats/${seatId}/chat`, { method: 'PUT' }, token),

  getChatUrl: (token: string): Promise<ChatUrlOut> =>
    fetchApi('/portal/chat-url', {}, token),

  getServerUsers: (token: string, serverName: string): Promise<ServerUserOut[]> =>
    fetchApi(`/portal/servers/${serverName}/users`, {}, token),

  addServerUser: (token: string, serverName: string, email: string): Promise<ServerUserOut> =>
    fetchApi(`/portal/servers/${serverName}/users`, {
      method: 'POST',
      body: JSON.stringify({ auth0_email: email }),
    }, token),

  removeServerUser: (token: string, serverName: string, email: string): Promise<void> =>
    fetchApi(`/portal/servers/${serverName}/users?email=${encodeURIComponent(email)}`, {
      method: 'DELETE',
    }, token),

  getChatUsage: (token: string): Promise<AppApiPortalPortalSchemasUsageStatsOut> =>
    fetchApi('/portal/chat-usage', {}, token),

  updateBudget: (token: string, data: BudgetUpdateInput): Promise<BudgetUpdateInput> =>
    fetchApi('/portal/budget', {
      method: 'PUT',
      body: JSON.stringify(data),
    }, token),

  getUsageHistory: (token: string): Promise<MonthlyUsageOut[]> =>
    fetchApi('/portal/usage-history', {}, token),

  getAiQuotaStatus: (token: string): Promise<AiQuotaStatusOut> =>
    fetchApi('/portal/ai-quota-status', {}, token),

  getServerLogs: (token: string, serverName: string): Promise<PortalMcpLogOut[]> =>
    fetchApi(`/portal/servers/${serverName}/logs`, {}, token),

  getBranding: (token: string): Promise<PortalBrandingOut> =>
    fetchApi('/portal/branding', {}, token),

  getWorkflows: (token: string): Promise<PortalWorkflowOut[]> =>
    fetchApi('/portal/workflows', {}, token),

  getWorkflowDetail: (token: string, name: string): Promise<PortalWorkflowDetailOut> =>
    fetchApi(`/portal/workflows/${name}`, {}, token),

  instantAddWorkflow: (token: string, name: string): Promise<PortalWorkflowDetailOut> =>
    fetchApi(`/portal/workflows/${name}/instant-add`, { method: 'POST' }, token),

  removeWorkflow: (token: string, name: string): Promise<void> =>
    fetchApi(`/portal/workflows/${name}/remove`, { method: 'DELETE' }, token),

  requestWorkflowAccess: (
    token: string,
    name: string,
    data: {
      variable_values?: Record<string, unknown>;
      server_configs?: { server_id: string; seat_id?: string; environment_id?: string; base_url?: string }[];
      message?: string;
    },
    file?: File | null,
    attachmentRole?: string,
  ): Promise<PortalAccessRequestOut> => {
    const form = new FormData();
    form.append('payload', JSON.stringify(data));
    if (file) {
      form.append('file', file, file.name);
      if (attachmentRole) form.append('attachment_role', attachmentRole);
    }
    // Multipart: bypass fetchApi (which forces JSON content-type) so fetch can set the boundary.
    return fetch(`${API_URL}/portal/workflows/${name}/request-access`, {
      method: 'POST',
      headers: token ? { Authorization: `Bearer ${token}` } : {},
      body: form,
    }).then(async (res) => {
      if (!res.ok) {
        const text = await res.text().catch(() => '');
        throw new Error(text || `HTTP ${res.status}`);
      }
      return res.json();
    });
  },

  triggerWorkflow: (token: string, name: string): Promise<void> =>
    fetchApi(`/portal/workflows/${name}/trigger`, { method: 'POST' }, token),

  getWorkflowExecutions: (token: string, name: string): Promise<PortalWorkflowExecutionOut[]> =>
    fetchApi(`/portal/workflows/${name}/executions`, {}, token),

  updateWorkflowConfig: (
    token: string,
    name: string,
    data: { variable_values?: Record<string, unknown>; server_configs?: { server_id: string; seat_id?: string; environment_id?: string; base_url?: string }[] },
  ): Promise<PortalWorkflowDetailOut> =>
    fetchApi(`/portal/workflows/${name}/config`, {
      method: 'PUT',
      body: JSON.stringify(data),
    }, token),

  getWorkflowUsers: (token: string, name: string): Promise<ServerUserOut[]> =>
    fetchApi(`/portal/workflows/${name}/users`, {}, token),

  addWorkflowUser: (token: string, name: string, email: string): Promise<ServerUserOut> =>
    fetchApi(`/portal/workflows/${name}/users`, {
      method: 'POST',
      body: JSON.stringify({ auth0_email: email }),
    }, token),

  removeWorkflowUser: (token: string, name: string, email: string): Promise<void> =>
    fetchApi(`/portal/workflows/${name}/users?email=${encodeURIComponent(email)}`, {
      method: 'DELETE',
    }, token),

  getVisualizationData: (token: string, name: string, nodeId: string): Promise<PortalVisualizationDataOut> =>
    fetchApi(`/portal/workflows/${name}/visualizations/${nodeId}`, {}, token),

  // Anonymous probe — returns only { known } so customers can't be enumerated by domain.
  getLoginInfo: (email: string): Promise<LoginInfoProbeOut> =>
    fetchApi(`/login_info?email=${encodeURIComponent(email)}`),

  // Authenticated probe for the signed-in user's joinable org (if any).
  getJoinableOrg: (token: string): Promise<JoinableOrgOut | null> =>
    fetchApi('/login_info/joinable', {}, token),

  orgSignup: (
    token: string,
    data: { organization_name: string; email: string },
  ): Promise<OrgSignupOut> =>
    fetchApi('/org_signup', { method: 'POST', body: JSON.stringify(data) }, token),

  orgJoin: (token: string, email: string): Promise<OrgJoinOut> =>
    fetchApi('/org_join', { method: 'POST', body: JSON.stringify({ email }) }, token),

  analyzeWorkflowGeneration: (token: string, data: AnalyzeRequest): Promise<AnalyzeResponse> =>
    fetchApi('/workflow_generation/analyze', {
      method: 'POST',
      body: JSON.stringify(data),
    }, token),

  createWorkflowGenerationRequest: (
    token: string,
    data: WorkflowGenerationRequestCreate,
  ): Promise<WorkflowGenerationRequestOut> =>
    fetchApi('/workflow_generation_request', {
      method: 'POST',
      body: JSON.stringify(data),
    }, token),
};
